import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { currencyCodes } from '~/globals';
import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Alert,
  AlertColor,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type Theme, alpha } from '@mui/material/styles';

import PageHeader from '~/components/Common/Elements/PageHeader';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import MarkdownEditor from '~/components/Common/Forms/widgets/MarkdownEditor';
import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import VoucherCheck from '~/components/Vouchers/VoucherCheck';
import VoucherCloneDialog from '~/components/Vouchers/VoucherCloneDialog';
import VoucherConditions from '~/components/Vouchers/VoucherConditions';
import VoucherLongExpiryPromoAdminAlert from '~/components/Vouchers/VoucherLongExpiryPromoAdminAlert';
import VoucherRecentRequests from '~/components/Vouchers/VoucherRecentRequests';
import VoucherRewards from '~/components/Vouchers/VoucherRewards';
import VoucherUsers from '~/components/Vouchers/VoucherUsers';
import { buildSuggestedClonedVoucherName } from '~/components/Vouchers/voucherCloneUtils';

import VoucherFormNavigation from '~/pages/Vouchers/VoucherFormNavigation';

import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import { useDeleteVoucher } from '~/hooks/apis/promo/useDeleteVoucher';
import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import {
  CreateVoucherPayload,
  CreateVoucherResponse,
  PromoMeta,
  UpdateVoucherPayload,
  UpdateVoucherResponse,
  Voucher,
  VoucherUserExpiryApiPayload,
  createVoucher,
  getVoucher,
  updateVoucher,
} from '~/services/PromoService';

import ApiError from '~/types/ApiError';

import {
  getDefaultAddVoucherUsersExpiryDate,
  normalizeFormDateStringToIso,
  normalizeToISODate,
} from '~/utils/dateFormatter';

type RouteParams = {
  voucherId?: string;
};

type VoucherFormLocationState = { fromVoucherCreate?: { voucherName: string } } | null | undefined;

type VoucherReward = NonNullable<CreateVoucherPayload['voucher_rewards']>[number];
type VoucherCondition = NonNullable<CreateVoucherPayload['voucher_conditions']>[number];
type VoucherToggleValue = string;
type VoucherToggleOption = {
  key: string;
  isActive: boolean;
  internalDescription: string;
  internalDescriptionLong: string;
  isBeingDeprecated?: boolean;
};
type VoucherWarning = {
  code?: string;
  message?: string;
  level?: string;
};

type VoucherApiFields = {
  voucherName?: string;
  voucherTitle?: string;
  voucherDescription?: string;
  voucherWarnings?: Array<VoucherWarning>;
  currencyCode?: string;
  voucherRewards?: Array<VoucherReward>;
  voucherConditions?: Array<VoucherCondition>;
  voucherToggles?: Array<VoucherToggleValue>;
  showVoucherFromDate?: string;
  theme?: VoucherTheme;
  productType?: VoucherProductType;
  product_type?: VoucherProductType;
  voucher_name?: string;
  voucher_title?: string;
  voucher_description?: string;
  currency_code?: string;
  voucher_rewards?: Array<VoucherReward>;
  voucher_conditions?: Array<VoucherCondition>;
  voucher_toggles?: Array<VoucherToggleValue>;
  show_voucher_from_date?: string;
};

type VoucherTheme = NonNullable<Voucher['theme']>;
type VoucherProductType = NonNullable<Voucher['productType']>;
type VoucherProductTypeMetaRow = PromoMeta['voucher_product_types'][number];

const VOUCHER_THEME_OPTIONS: Array<VoucherTheme> = ['default', 'luxplus', 'societe'];

/** Only used when promo meta failed to load or returned an empty list. */
const FALLBACK_VOUCHER_PRODUCT_TYPE_KEYS: Array<VoucherProductType> = [
  'flight',
  'generic',
  'hotel',
  'cruise',
  'tour',
  'carhire',
];

const VOUCHER_USER_EXPIRY_NOT_SET_WARNING_CODE: VoucherWarning['code'] = 'VOUCHER_USER_EXPIRY_NOT_SET';

type VoucherUserExpiryMode = 'days' | 'date';

function voucherUserExpiryWarningFieldSx(theme: Theme) {
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: alpha(theme.palette.warning.main, 0.12),
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.warning.main,
      borderWidth: 2,
    },
  };
}

type VoucherFormValues = {
  voucherName: string;
  voucherTitle: string;
  voucherDescription: string;
  /** Internal support reference; stored uppercased server-side. */
  caseId: string;
  /** Internal note (why the voucher exists). */
  reason: string;
  currencyCode: string;
  voucherRewards: Array<VoucherReward>;
  voucherConditions: Array<VoucherCondition>;
  voucherToggles: Array<VoucherToggleValue>;
  showVoucherFromDate: string;
  voucherUserExpiresAt: string;
  voucherUserExpiresInDays: string;
  theme: VoucherTheme;
  productType: VoucherProductType;
};

function voucherUserExpiryToApiPayload(
  voucherUserExpiresAt: string,
  voucherUserExpiresInDays: string,
  mode: VoucherUserExpiryMode,
): VoucherUserExpiryApiPayload {
  if (mode === 'date') {
    const expiresAtTrimmed = voucherUserExpiresAt.trim();
    return {
      voucher_user_expires_at: expiresAtTrimmed ? normalizeToISODate(expiresAtTrimmed) : null,
      voucher_user_expires_in_days: null,
    };
  }
  const expiresInDaysTrimmed = voucherUserExpiresInDays.trim();
  let expiresInDaysNumber: number | undefined;
  if (expiresInDaysTrimmed !== '') {
    const parsed = Number.parseInt(expiresInDaysTrimmed, 10);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      expiresInDaysNumber = parsed;
    }
  }
  return {
    voucher_user_expires_at: null,
    voucher_user_expires_in_days:
      expiresInDaysTrimmed === '' ? null : expiresInDaysNumber !== undefined ? expiresInDaysNumber : null,
  };
}

const DEFAULT_REWARD_AMOUNT = 50;
const DEFAULT_MIN_SPEND = 500;
const DEFAULT_REWARD_PRODUCTS: Array<VoucherReward['products'][number]> = ['hotel-ALL', 'tour-ALL', 'cruise-ALL'];

const buildDefaultFormValues = (): VoucherFormValues => ({
  voucherName: '',
  voucherTitle: '',
  voucherDescription: '',
  caseId: '',
  reason: '',
  currencyCode: 'AUD',
  voucherRewards: [
    {
      id_voucher_reward: uuid(),
      reward_amount: DEFAULT_REWARD_AMOUNT,
      products: DEFAULT_REWARD_PRODUCTS,
      min_spend: undefined,
      min_spend_products: [...DEFAULT_REWARD_PRODUCTS] as NonNullable<VoucherReward['min_spend_products']>,
    },
  ],
  voucherConditions: [],
  voucherToggles: [],
  showVoucherFromDate: '',
  voucherUserExpiresAt: '',
  voucherUserExpiresInDays: '',
  theme: 'default',
  productType: 'generic',
});

/**
 * True when the API value is only a calendar start date (no specific time of day):
 * a bare YYYY-MM-DD, or that same meaning sent as start-of-day UTC.
 */
function isStartDateOnlyInApi(s: string): boolean {
  const t = s.trim();
  if (!t) return true;
  if (!t.includes('T')) return true;
  return /T00:00:00(\.0+)?Z$/.test(t) || /T00:00:00(\.0+)?\+00:00$/.test(t);
}

/** Maps API "show from" to form: date field by default; date+time if a specific instant is stored. */
function getShowVoucherFromFieldsFromApi(raw: string | undefined | null): { value: string; includeTime: boolean } {
  if (!raw?.toString().trim()) {
    return { value: '', includeTime: false };
  }
  const s = raw.toString().trim();
  if (isStartDateOnlyInApi(s)) {
    return {
      value: s.slice(0, 10),
      includeTime: false,
    };
  }
  const d = dayjs(s);
  return {
    value: d.isValid() ? d.format('YYYY-MM-DDTHH:mm') : '',
    includeTime: true,
  };
}

const parseVoucherApiResponse = (voucher: Voucher): VoucherFormValues => {
  const data = voucher as VoucherApiFields;
  const raw = voucher as unknown as Record<string, unknown>;
  const rawCurrencyCode = data.currencyCode || data.currency_code;
  const rawExpiresInDays = raw.voucherUserExpiresInDays ?? raw.voucher_user_expires_in_days;
  const voucherWithCase = voucher as Voucher & { case_id?: string };
  const caseFromApi = voucherWithCase.caseId ?? voucherWithCase.case_id;
  return {
    voucherName: data.voucherName || data.voucher_name || '',
    voucherTitle: data.voucherTitle || data.voucher_title || '',
    voucherDescription: data.voucherDescription || data.voucher_description || '',
    caseId: caseFromApi != null && String(caseFromApi).trim() !== '' ? String(caseFromApi) : '',
    reason: voucher.reason != null && String(voucher.reason).trim() !== '' ? String(voucher.reason) : '',
    currencyCode: rawCurrencyCode && currencyCodes.includes(rawCurrencyCode) ? rawCurrencyCode : 'AUD',
    voucherRewards: (data.voucherRewards ?? data.voucher_rewards ?? []).map((reward) => ({
      ...reward,
      id_voucher_reward: reward.id_voucher_reward || uuid(),
    })),
    voucherConditions: (data.voucherConditions ?? data.voucher_conditions ?? []).map((condition) => ({
      ...condition,
      id_voucher_condition: condition.id_voucher_condition || uuid(),
    })),
    voucherToggles: data.voucherToggles ?? data.voucher_toggles ?? [],
    showVoucherFromDate: getShowVoucherFromFieldsFromApi(data.showVoucherFromDate || data.show_voucher_from_date).value,
    voucherUserExpiresAt: String(raw.voucherUserExpiresAt ?? raw.voucher_user_expires_at ?? ''),
    voucherUserExpiresInDays: rawExpiresInDays != null && rawExpiresInDays !== '' ? String(rawExpiresInDays) : '',
    theme: data.theme || 'default',
    productType: data.productType || data.product_type || 'generic',
  };
};

const getVoucherWarningSeverity = (level?: string): AlertColor => {
  if (level === 'error' || level === 'warning' || level === 'info' || level === 'success') {
    return level;
  }
  return 'warning';
};

/** `request()` maps JSON error bodies onto `ApiError` (`message`, `status`, `errors`). */
function formatVoucherSaveFailure(error: unknown): string {
  if (error instanceof ApiError) {
    const parts: Array<string> = [error.message];
    if (error.status != null) {
      parts.push(`HTTP ${error.status}`);
    }
    const { errors } = error;
    let bodyDetails: string | null = null;
    if (errors !== undefined && errors !== null) {
      if (typeof errors === 'string') {
        const trimmed = errors.trim();
        bodyDetails = trimmed.length > 0 ? trimmed : null;
      } else if (Array.isArray(errors)) {
        bodyDetails = errors.length > 0 ? JSON.stringify(errors, null, 2) : null;
      } else {
        const serialized = JSON.stringify(errors, null, 2);
        bodyDetails = serialized !== '{}' && serialized !== 'null' ? serialized : null;
      }
    }
    if (bodyDetails) {
      parts.push(bodyDetails);
    }
    return parts.join(' — ');
  }
  if (error instanceof Error) {
    return error.message;
  }
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

export default function VoucherFormPage() {
  const { voucherId } = useParams<RouteParams>();
  const isEdit = Boolean(voucherId);
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { control, handleSubmit, reset, formState, watch, setValue, getValues, trigger } = useForm<VoucherFormValues>({
    defaultValues: buildDefaultFormValues(),
  });
  const { isDirty, isSubmitting } = formState;

  /** When true, the next in-app navigation is allowed without the unsaved-changes confirm (e.g. after delete). */
  const bypassUnsavedNavigationBlockRef = useRef(false);
  /** Values loaded from API (edit mode) — used to warn before clearing case ID / reason. */
  const initialInternalFieldsRef = useRef<{ caseId: string; reason: string }>({ caseId: '', reason: '' });
  const watchedVoucherUserExpiresAt = watch('voucherUserExpiresAt');
  const watchedVoucherUserExpiresInDays = watch('voucherUserExpiresInDays');

  const [showAdvanceDescription, setShowAdvanceDescription] = useState(false);
  /** When true, the show-from field includes a time; when false, start date only (the usual case). */
  const [showVoucherFromDateIncludeTime, setShowVoucherFromDateIncludeTime] = useState(false);
  const [voucherTogglesInputFocused, setVoucherTogglesInputFocused] = useState(false);
  const [voucherTogglesListOpen, setVoucherTogglesListOpen] = useState(false);
  const [voucherUsers, setVoucherUsers] = useState<Array<string>>([]);
  const [expiredAt, setExpiredAt] = useState('');
  const [voucherUserExpiryMode, setVoucherUserExpiryMode] = useState<VoucherUserExpiryMode>('days');
  /** Must stay in sync with `voucherUserExpiryMode` before paint (e.g. RHF `setValue` + `shouldValidate`). */
  const voucherUserExpiryModeRef = useRef<VoucherUserExpiryMode>('days');
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [descriptionEditorKey, setDescriptionEditorKey] = useState(0);
  const brand = useCurrentBrand();
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();

  const { data: voucherResponse, isLoading: isVoucherLoading } = useTanstackQuery({
    queryKey: ['voucher', voucherId],
    queryFn: async () => {
      if (!voucherId) {
        return null;
      }
      return await getVoucher(voucherId);
    },
    enabled: Boolean(voucherId),
  });

  const voucher: Voucher | null = useMemo(() => {
    return voucherResponse?.result ?? null;
  }, [voucherResponse]);

  useEffect(() => {
    if (voucher) {
      const v = voucher as Voucher & { case_id?: string };
      initialInternalFieldsRef.current = {
        caseId: String(v.caseId ?? v.case_id ?? '').trim(),
        reason: String(v.reason ?? '').trim(),
      };
    } else if (!isEdit) {
      initialInternalFieldsRef.current = { caseId: '', reason: '' };
    }
  }, [voucher, isEdit]);

  const parentVoucherId = useMemo(() => {
    if (!voucher) {
      return '';
    }
    const raw = (voucher as Voucher & { clonedFromId?: string | null }).clonedFromId;
    return typeof raw === 'string' ? raw.trim() : '';
  }, [voucher]);

  const { data: parentVouchersMap } = useGetVouchersByIds({
    voucherIds: parentVoucherId ? [parentVoucherId] : [],
    enabled: Boolean(parentVoucherId),
  });
  const parentVoucherName = parentVoucherId ? parentVouchersMap?.get(parentVoucherId)?.voucherName?.trim() : undefined;

  const voucherAuditFields = useMemo(() => {
    if (!voucher) {
      return {
        createdByUserId: '',
        modifiedByUserId: '',
        createdAt: '',
        updatedAt: '',
      };
    }
    const v = voucher as Voucher & {
      created_by?: string;
      updated_by?: string;
      modified_by?: string;
      created_at?: string;
      updated_at?: string;
    };
    return {
      createdByUserId: (v.createdBy ?? v.created_by ?? '').trim(),
      modifiedByUserId: (v.updatedBy ?? v.updated_by ?? v.modified_by ?? '').trim(),
      createdAt: v.createdAt ?? v.created_at ?? '',
      updatedAt: v.updatedAt ?? v.updated_at ?? '',
    };
  }, [voucher]);

  const voucherAuditUserIds = useMemo(
    () => [voucherAuditFields.createdByUserId, voucherAuditFields.modifiedByUserId].filter(Boolean),
    [voucherAuditFields.createdByUserId, voucherAuditFields.modifiedByUserId],
  );

  const { data: voucherAuditUsersById = new Map<string, App.UserSummary>() } = useGetUsersSummaryByIds({
    userIds: voucherAuditUserIds,
    brand,
    enabled: Boolean(isEdit && voucher && voucherAuditUserIds.length > 0),
  });

  const voucherWarnings = useMemo(() => {
    if (!voucher) {
      return [];
    }
    const voucherData = voucher as VoucherApiFields;
    return voucherData.voucherWarnings ?? [];
  }, [voucher]);

  const highlightVoucherUserExpiryFields = useMemo(
    () => voucherWarnings.some((w) => w.code === VOUCHER_USER_EXPIRY_NOT_SET_WARNING_CODE),
    [voucherWarnings],
  );

  // Populate form from API data — guarded to run only once per voucherId
  useEffect(() => {
    if (!isEdit) {
      const defaults = buildDefaultFormValues();
      reset(defaults);
      setVoucherUsers([]);
      setExpiredAt('');
      voucherUserExpiryModeRef.current = 'days';
      setVoucherUserExpiryMode('days');
      setShowVoucherFromDateIncludeTime(false);
      setDescriptionEditorKey((prev) => prev + 1);
      return;
    }
    if (voucher) {
      const data = voucher as VoucherApiFields;
      const raw = data.showVoucherFromDate || data.show_voucher_from_date;
      const { includeTime } = getShowVoucherFromFieldsFromApi(raw);
      const values = parseVoucherApiResponse(voucher);
      reset(values);
      setVoucherUsers([]);
      setExpiredAt('');
      const nextExpiryMode: VoucherUserExpiryMode = values.voucherUserExpiresAt.trim() ? 'date' : 'days';
      voucherUserExpiryModeRef.current = nextExpiryMode;
      setVoucherUserExpiryMode(nextExpiryMode);
      setShowVoucherFromDateIncludeTime(includeTime);
      setDescriptionEditorKey((prev) => prev + 1);
    }
  }, [isEdit, voucher, voucherId, reset]);

  const voucherNameValue = watch('voucherName');
  const watchedProductType = watch('productType');
  const watchedShowVoucherFromDate = watch('showVoucherFromDate');
  const voucherToggles = watch('voucherToggles');
  const voucherRewards = watch('voucherRewards');
  const voucherConditions = watch('voucherConditions');
  const currencyCode = watch('currencyCode');

  const voucherToggleOptions: Array<VoucherToggleOption> = promoMeta?.voucher_toggles ?? [];

  const voucherProductTypeMenuRows: Array<VoucherProductTypeMetaRow> = useMemo(() => {
    const fromMeta = promoMeta?.voucher_product_types;
    if (fromMeta && fromMeta.length > 0) {
      const keys = new Set<VoucherProductType>(fromMeta.map((row) => row.key as VoucherProductType));
      if (!keys.has(watchedProductType) && isEdit) {
        return [
          ...fromMeta,
          {
            key: watchedProductType,
            name: `[UNKNOWN] ${watchedProductType}`,
            description:
              'This product type is not in current voucher metadata and will be preserved unless you change it.',
          },
        ];
      }
      return fromMeta;
    }
    if (isPromoMetaLoading) {
      return [];
    }
    return FALLBACK_VOUCHER_PRODUCT_TYPE_KEYS.map((key) => ({
      key,
      name: key,
      description: '',
    }));
  }, [promoMeta, isPromoMetaLoading, watchedProductType, isEdit]);

  const selectedUnknownVoucherToggleOptions = useMemo<Array<VoucherToggleOption>>(() => {
    const knownToggleKeys = new Set(voucherToggleOptions.map((option) => option.key));
    return voucherToggles
      .filter((key) => !knownToggleKeys.has(key))
      .map((key) => ({
        key,
        isActive: false,
        internalDescription: `[UNKNOWN] ${key}`,
        internalDescriptionLong:
          'This toggle key is not in current voucher metadata and will be preserved unless explicitly removed.',
      }));
  }, [voucherToggles, voucherToggleOptions]);

  const voucherToggleOptionsWithUnknown = useMemo<Array<VoucherToggleOption>>(() => {
    const allOptions = [...voucherToggleOptions, ...selectedUnknownVoucherToggleOptions];
    return allOptions.sort((a, b) => {
      if (a.isBeingDeprecated !== b.isBeingDeprecated) {
        return Number(a.isBeingDeprecated ?? false) - Number(b.isBeingDeprecated ?? false);
      }
      return a.internalDescription.localeCompare(b.internalDescription);
    });
  }, [voucherToggleOptions, selectedUnknownVoucherToggleOptions]);

  const selectedVoucherToggleOptions = useMemo<Array<VoucherToggleOption>>(() => {
    const optionByKey = new Map(voucherToggleOptionsWithUnknown.map((option) => [option.key, option] as const));
    return voucherToggles
      .map((key) => optionByKey.get(key))
      .filter((option): option is VoucherToggleOption => Boolean(option));
  }, [voucherToggles, voucherToggleOptionsWithUnknown]);

  const getVoucherToggleOptionLabel = (option: VoucherToggleOption) =>
    `${option.isBeingDeprecated ? '[DEPRECATED] - ' : ''}${!option.isActive ? '[INACTIVE] - ' : ''}${
      option.internalDescription
    }`;

  const showVoucherToggleLongDescriptions =
    showAdvanceDescription || voucherTogglesInputFocused || voucherTogglesListOpen;

  const voucherToggleDescriptionPanelRows = useMemo(() => {
    const selectedKeys = new Set(voucherToggles);
    const selectedRows = selectedVoucherToggleOptions.map((option) => ({ option, isSelected: true as const }));
    const otherRows = voucherToggleOptionsWithUnknown
      .filter((o) => !selectedKeys.has(o.key))
      .map((option) => ({ option, isSelected: false as const }));
    return [...selectedRows, ...otherRows];
  }, [voucherToggles, selectedVoucherToggleOptions, voucherToggleOptionsWithUnknown]);

  useEffect(() => {
    if (voucherUserExpiryModeRef.current === 'date') {
      void trigger('voucherUserExpiresAt');
    }
  }, [watchedShowVoucherFromDate, trigger]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    const unblock = history.block(() => {
      if (bypassUnsavedNavigationBlockRef.current) {
        bypassUnsavedNavigationBlockRef.current = false;
        return undefined;
      }
      const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave this page?');
      if (!shouldLeave) {
        return false;
      }
      return undefined;
    });
    return () => unblock();
  }, [isDirty, history]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'users') {
      const usersElement = document.getElementById('users');
      if (usersElement) {
        usersElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [location.hash]);

  const createMutation = useMutation<CreateVoucherResponse, Error, CreateVoucherPayload>({
    mutationFn: async (payload) => createVoucher(payload),
  });

  const updateMutation = useMutation<UpdateVoucherResponse, Error, { id: string; payload: UpdateVoucherPayload }>({
    mutationFn: async ({ id, payload }) => updateVoucher(id, payload),
  });

  const createVoucherUsersMutation = useCreateVoucherUsers();
  const deleteVoucherMutation = useDeleteVoucher();

  const onSubmit = async (data: VoucherFormValues) => {
    try {
      if (isEdit) {
        const initial = initialInternalFieldsRef.current;
        const clearingCaseId = initial.caseId !== '' && data.caseId.trim() === '';
        const clearingReason = initial.reason !== '' && data.reason.trim() === '';
        if (clearingCaseId || clearingReason) {
          const clearedLabels: Array<string> = [];
          if (clearingCaseId) {
            clearedLabels.push('case ID');
          }
          if (clearingReason) {
            clearedLabels.push('reason');
          }
          const proceed = window.confirm(
            `You are about to clear the voucher ${clearedLabels.join(' and ')}. Continue?`,
          );
          if (!proceed) {
            return;
          }
        }
      }

      const normalizedRewards = data.voucherRewards.map((reward): VoucherReward => {
        const minSpendProducts = (reward.min_spend_products ?? []).filter(Boolean);
        return {
          ...reward,
          currency_code: (reward.currency_code ||
            data.currencyCode ||
            'AUD') as unknown as VoucherReward['currency_code'],
          reward_amount: Number(reward.reward_amount),
          min_spend: reward.min_spend == null ? undefined : Number(reward.min_spend),
          ...(minSpendProducts.length > 0 ? { min_spend_products: minSpendProducts } : {}),
        };
      });

      let savedVoucherId = voucherId || '';
      const rawShowFrom = (data.showVoucherFromDate || '').trim();
      const showVoucherFromDateValue = showVoucherFromDateIncludeTime
        ? normalizeFormDateStringToIso(data.showVoucherFromDate)
        : rawShowFrom
        ? normalizeToISODate(rawShowFrom.length === 10 ? rawShowFrom : rawShowFrom.slice(0, 10) || rawShowFrom)
        : '';
      const { voucher_user_expires_at, voucher_user_expires_in_days } = voucherUserExpiryToApiPayload(
        data.voucherUserExpiresAt,
        data.voucherUserExpiresInDays,
        voucherUserExpiryMode,
      );

      if (isEdit) {
        const payload: UpdateVoucherPayload & {
          voucher_toggles?: CreateVoucherPayload['voucher_toggles'];
          voucher_user_expires_at?: string | null;
          voucher_user_expires_in_days?: number | null;
        } = {
          ...(data.voucherName.trim() ? { voucher_name: data.voucherName.trim() } : {}),
          voucher_title: data.voucherTitle,
          voucher_description: data.voucherDescription,
          voucher_rewards: normalizedRewards,
          voucher_conditions: data.voucherConditions,
          voucher_toggles: data.voucherToggles as CreateVoucherPayload['voucher_toggles'],
          ...(showVoucherFromDateValue ? { show_voucher_from_date: showVoucherFromDateValue } : {}),
          voucher_user_expires_at,
          voucher_user_expires_in_days,
          theme: data.theme as UpdateVoucherPayload['theme'],
          product_type: data.productType as UpdateVoucherPayload['product_type'],
          case_id: data.caseId.trim() ? data.caseId.trim().toUpperCase() : null,
          reason: data.reason.trim() ? data.reason.trim() : null,
        };
        await updateMutation.mutateAsync({ id: voucherId || '', payload });
      } else {
        const payload: CreateVoucherPayload & {
          voucher_user_expires_at?: string | null;
          voucher_user_expires_in_days?: number | null;
        } = {
          voucher_name: data.voucherName.trim(),
          voucher_title: data.voucherTitle,
          voucher_description: data.voucherDescription,
          voucher_rewards: normalizedRewards,
          voucher_conditions: data.voucherConditions,
          voucher_toggles: data.voucherToggles as CreateVoucherPayload['voucher_toggles'],
          ...(showVoucherFromDateValue ? { show_voucher_from_date: showVoucherFromDateValue } : {}),
          voucher_user_expires_at: voucher_user_expires_at ?? undefined,
          voucher_user_expires_in_days: voucher_user_expires_in_days ?? undefined,
          theme: data.theme as CreateVoucherPayload['theme'],
          product_type: data.productType as CreateVoucherPayload['product_type'],
          ...(data.caseId.trim() ? { case_id: data.caseId.trim().toUpperCase() } : {}),
          ...(data.reason.trim() ? { reason: data.reason.trim() } : {}),
        };
        const createResponse = await createMutation.mutateAsync(payload);
        savedVoucherId = createResponse?.result?.voucherId || '';
      }

      if (voucherUsers.length > 0 && savedVoucherId) {
        const resolvedExpiredAt = expiredAt.trim()
          ? normalizeToISODate(expiredAt)
          : normalizeToISODate(
              getDefaultAddVoucherUsersExpiryDate({
                voucherUserExpiresAt: voucherUserExpiryMode === 'date' ? data.voucherUserExpiresAt : '',
                voucherUserExpiresInDays: voucherUserExpiryMode === 'days' ? data.voucherUserExpiresInDays : '',
              }),
            );
        await createVoucherUsersMutation.mutateAsync({
          voucher_id: savedVoucherId,
          expired_at: resolvedExpiredAt,
          voucher_users: voucherUsers.map((userId) => ({ user_id: userId })),
        });
      }

      reset(data);
      if (isEdit) {
        initialInternalFieldsRef.current = {
          caseId: data.caseId.trim(),
          reason: data.reason.trim(),
        };
      }
      enqueueSnackbar(isEdit ? 'Updated voucher successfully' : 'Created voucher successfully', {
        variant: 'success',
      });

      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      if (isEdit && voucherId) {
        queryClient.invalidateQueries({ queryKey: ['voucher', voucherId] });
      }

      if (!isEdit && savedVoucherId) {
        bypassUnsavedNavigationBlockRef.current = true;
        history.replace({
          pathname: `/marketing/vouchers/${savedVoucherId}`,
          state: { fromVoucherCreate: { voucherName: data.voucherName.trim() } },
        });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to save voucher: ${formatVoucherSaveFailure(error)}`, { variant: 'error' });
    }
  };

  const handleCancelDelete = () => {
    if (!deleteVoucherMutation.isPending) {
      setDeleteDialogOpen(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!voucherId) {
      enqueueSnackbar('Unable to delete voucher: missing voucher id.', { variant: 'error' });
      return;
    }
    try {
      await deleteVoucherMutation.mutateAsync(voucherId);
      enqueueSnackbar(`Deleted voucher ${voucherNameValue || voucherId}.`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      queryClient.removeQueries({ queryKey: ['voucher', voucherId] });
      setDeleteDialogOpen(false);
      bypassUnsavedNavigationBlockRef.current = true;
      history.push('/marketing/vouchers');
    } catch (error) {
      enqueueSnackbar(`Failed to delete voucher: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  if (isEdit && isVoucherLoading) {
    return <Spinner />;
  }

  const fromCreate = (location.state as VoucherFormLocationState)?.fromVoucherCreate;
  const createdVoucherName = fromCreate?.voucherName?.trim();

  return (
    <Container maxWidth="xl">
      <PageHeader title={isEdit ? 'Edit Voucher' : 'Create Voucher'}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" flexWrap="wrap" spacing={2} useFlexGap>
          <VoucherFormNavigation
            isSubmitting={isSubmitting}
            isEdit={isEdit}
            showAdvanceDescription={showAdvanceDescription}
            onBack={() => history.push('/marketing/vouchers')}
            onSubmit={handleSubmit(onSubmit)}
            onToggleAdvancedDescription={() => setShowAdvanceDescription(!showAdvanceDescription)}
            onOpenClone={isEdit && voucherId ? () => setCloneDialogOpen(true) : undefined}
          />
          {isEdit && voucher && (voucherAuditFields.createdByUserId || voucherAuditFields.modifiedByUserId) ? (
            <Stack alignItems="flex-end" spacing={0.25} sx={{ textAlign: 'right' }}>
              {voucherAuditFields.createdByUserId ? (
                <Typography variant="caption" color="text.secondary" component="div">
                  Created by{' '}
                  <Link to={`/users/${voucherAuditFields.createdByUserId}`}>
                    {voucherAuditUsersById.get(voucherAuditFields.createdByUserId)?.full_name ??
                      voucherAuditFields.createdByUserId}
                  </Link>
                  {voucherAuditFields.createdAt ? (
                    <Box component="span" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                      ({dayjs(voucherAuditFields.createdAt).format('D MMM YYYY, h:mm a')})
                    </Box>
                  ) : null}
                </Typography>
              ) : null}
              {voucherAuditFields.modifiedByUserId ? (
                <Typography variant="caption" color="text.secondary" component="div">
                  Last modified by{' '}
                  <Link to={`/users/${voucherAuditFields.modifiedByUserId}`}>
                    {voucherAuditUsersById.get(voucherAuditFields.modifiedByUserId)?.full_name ??
                      voucherAuditFields.modifiedByUserId}
                  </Link>
                  {voucherAuditFields.updatedAt ? (
                    <Box component="span" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                      ({dayjs(voucherAuditFields.updatedAt).format('D MMM YYYY, h:mm a')})
                    </Box>
                  ) : null}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </PageHeader>

      {isEdit && voucherId && fromCreate ? (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          onClose={() => {
            const state = { ...((location.state as Record<string, unknown> | null) ?? {}) };
            delete state.fromVoucherCreate;
            history.replace({ ...location, state: Object.keys(state).length > 0 ? state : null });
          }}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" component="div">
            Voucher <strong>{createdVoucherName || voucherNameValue || voucherId}</strong> was created.{' '}
            <Link to={`/marketing/vouchers/${voucherId}`}>Edit voucher</Link>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {voucherId}
          </Typography>
        </Alert>
      ) : null}

      {parentVoucherId ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Parent voucher:{' '}
            <Link to={`/marketing/vouchers/${parentVoucherId}`}>{parentVoucherName || parentVoucherId}</Link>
          </Typography>
        </Box>
      ) : null}

      {isEdit && voucherId && (
        <VoucherCloneDialog
          open={cloneDialogOpen}
          onClose={() => setCloneDialogOpen(false)}
          sourceVoucherId={voucherId}
          sourceVoucherDisplayName={voucherNameValue.trim() || undefined}
          sourceVoucherRewards={voucherRewards}
          sourceVoucherCurrencyCode={currencyCode}
          suggestedNewVoucherName={buildSuggestedClonedVoucherName(voucherNameValue)}
          clonedFromVoucherId={voucher?.clonedFromId}
          onCloned={(newVoucherId) => {
            queryClient.invalidateQueries({ queryKey: ['voucher', newVoucherId] });
            history.push(`/marketing/vouchers/${newVoucherId}`);
          }}
        />
      )}

      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete voucher</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete voucher {voucherNameValue || voucherId}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCancelDelete} disabled={deleteVoucherMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmDelete}
            disabled={deleteVoucherMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Stack spacing={10}>
        {voucherWarnings.length > 0 && (
          <Stack direction="column" spacing={2}>
            {voucherWarnings.map((warning, index) => (
              <Alert
                key={`${warning.code ?? 'voucher-warning'}-${index}`}
                severity={getVoucherWarningSeverity(warning.level)}
                title={JSON.stringify(warning, null, 4)}
              >
                <AlertTitle>{warning.code ?? 'Voucher warning'}</AlertTitle>
                {warning.message || 'Please review this warning before proceeding.'}
              </Alert>
            ))}
          </Stack>
        )}
        <Stack spacing={2}>
          <PageSubheader title="Presentation" />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
            <Controller
              name="voucherName"
              control={control}
              rules={{
                validate: (value) => !!value.trim() || 'Voucher name is required',
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Voucher Name"
                  error={!!fieldState.error}
                  sx={{
                    ...(isEdit
                      ? {
                          '& .MuiInputBase-input': {
                            color: 'text.primary',
                          },
                        }
                      : {}),
                  }}
                  helperText={
                    fieldState.error?.message ||
                    (isEdit
                      ? 'Voucher name cannot be changed after creation'
                      : showAdvanceDescription
                      ? 'Name displayed for the voucher. This is used during processing and is a fallback value if the voucher title is not set'
                      : undefined)
                  }
                  fullWidth
                  required
                  disabled={isSubmitting}
                  slotProps={{
                    input: {
                      readOnly: isEdit,
                    },
                  }}
                />
              )}
            />
          </Stack>
          <Controller
            name="voucherTitle"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Voucher Title"
                helperText={
                  showAdvanceDescription
                    ? 'Optional title displayed for this voucher to the user when available.'
                    : undefined
                }
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
          <Controller
            name="voucherDescription"
            control={control}
            render={({ field }) => (
              <Stack spacing={1}>
                <Typography fontWeight={500}>Voucher Description</Typography>
                <MarkdownEditor
                  key={descriptionEditorKey}
                  {...field}
                  helperText={
                    showAdvanceDescription
                      ? 'Optional description shown to the user to explain voucher details. Supports markdown.'
                      : undefined
                  }
                  disabled={isSubmitting}
                />
              </Stack>
            )}
          />
          <PageSubheader title="Internal reference" />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="caseId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Case ID"
                  fullWidth
                  disabled={isSubmitting}
                  helperText={
                    showAdvanceDescription
                      ? 'Not shown to customers. Stored uppercased. Used as an internal reference (e.g. support case).'
                      : 'Internal only; stored uppercased.'
                  }
                />
              )}
            />
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reason"
                  fullWidth
                  disabled={isSubmitting}
                  helperText={
                    showAdvanceDescription
                      ? 'Internal note describing why this voucher exists (not shown to customers).'
                      : 'Internal note (not shown to customers).'
                  }
                />
              )}
            />
          </Stack>
          {!isPromoMetaLoading && promoMeta && promoMeta.voucher_toggles.length > 0 && (
            <Controller
              name="voucherToggles"
              control={control}
              render={({ field }) => (
                <Box>
                  <Autocomplete
                    multiple
                    options={voucherToggleOptionsWithUnknown}
                    isOptionEqualToValue={(option, value) => option.key === value.key}
                    getOptionLabel={getVoucherToggleOptionLabel}
                    value={selectedVoucherToggleOptions}
                    onOpen={() => setVoucherTogglesListOpen(true)}
                    onClose={() => setVoucherTogglesListOpen(false)}
                    onChange={(_event, newValue) => {
                      field.onChange(newValue.map((option) => option.key));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Voucher Toggles"
                        inputProps={{
                          ...params.inputProps,
                          onFocus: (e) => {
                            params.inputProps.onFocus?.(e as React.FocusEvent<HTMLInputElement>);
                            setVoucherTogglesInputFocused(true);
                          },
                          onBlur: (e) => {
                            params.inputProps.onBlur?.(e as React.FocusEvent<HTMLInputElement>);
                            setVoucherTogglesInputFocused(false);
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const label = getVoucherToggleOptionLabel(option);
                      return (
                        <li
                          {...props}
                          title={
                            option.isActive
                              ? option.internalDescriptionLong
                              : `Inactive - ${option.internalDescriptionLong}`
                          }
                          style={option.isBeingDeprecated ? { textDecoration: 'line-through' } : undefined}
                        >
                          {label}
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            {...tagProps}
                            label={`${option.isBeingDeprecated ? '[DEPRECATED] - ' : ''}${
                              !option.isActive ? '[INACTIVE] - ' : ''
                            }${option.internalDescription}`}
                            title={
                              option.isActive
                                ? option.internalDescriptionLong
                                : `Inactive - this toggle will not be applied - ${option.internalDescriptionLong}`
                            }
                            sx={option.isBeingDeprecated ? { textDecoration: 'line-through' } : undefined}
                          />
                        );
                      })
                    }
                    disabled={isSubmitting}
                  />
                  {showVoucherToggleLongDescriptions && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        maxHeight: 320,
                        overflow: 'auto',
                        bgcolor: 'action.hover',
                      }}
                    >
                      {voucherToggleDescriptionPanelRows.map(({ option, isSelected }) => (
                        <Box
                          key={option.key}
                          sx={{
                            mb: 1.5,
                            '&:last-child': { mb: 0 },
                            p: 1.5,
                            borderRadius: 1,
                            textDecoration: option.isBeingDeprecated ? 'line-through' : undefined,
                            ...(isSelected
                              ? {
                                  bgcolor: 'primary.main',
                                  color: 'primary.contrastText',
                                  border: 1,
                                  borderColor: 'primary.dark',
                                }
                              : {
                                  bgcolor: 'background.paper',
                                }),
                          }}
                        >
                          <Typography variant="subtitle2" component="div" color={isSelected ? 'inherit' : undefined}>
                            {getVoucherToggleOptionLabel(option)}
                          </Typography>
                          <Typography
                            variant="body2"
                            component="div"
                            sx={{
                              color: isSelected ? 'inherit' : undefined,
                              opacity: isSelected ? 0.95 : 1,
                              ...(isSelected ? {} : { color: 'text.secondary' }),
                            }}
                          >
                            {option.isActive
                              ? option.internalDescriptionLong
                              : `Inactive — this toggle will not be applied. ${option.internalDescriptionLong}`}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            />
          )}
          <Controller
            name="showVoucherFromDate"
            control={control}
            render={({ field }) => {
              const v = field.value || '';
              const inputValue = showVoucherFromDateIncludeTime
                ? v
                  ? v.length === 10
                    ? `${v}T00:00`
                    : v.slice(0, 16)
                  : ''
                : v
                ? v.slice(0, 10)
                : '';
              return (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    label="Show Voucher From Date"
                    type={showVoucherFromDateIncludeTime ? 'datetime-local' : 'date'}
                    value={inputValue}
                    onChange={(event) => field.onChange(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={showVoucherFromDateIncludeTime ? { step: 60 } : undefined}
                    helperText={
                      showAdvanceDescription
                        ? showVoucherFromDateIncludeTime
                          ? 'Optional date and time from which this voucher becomes visible to users. Leave empty for immediate visibility.'
                          : 'Optional: first date this voucher is visible. Most of the time the start time does not matter—use Set time only when you need a specific time. Leave empty for immediate visibility.'
                        : undefined
                    }
                    fullWidth
                    disabled={isSubmitting}
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={showVoucherFromDateIncludeTime}
                        onChange={(_, checked) => {
                          setShowVoucherFromDateIncludeTime(checked);
                          const current = (getValues('showVoucherFromDate') || '').trim();
                          if (checked) {
                            // Empty means "immediate visibility" — do not invent today's date at midnight.
                            if (!current) {
                              field.onChange('');
                              return;
                            }
                            const datePart = current.length >= 10 ? current.slice(0, 10) : dayjs().format('YYYY-MM-DD');
                            field.onChange(`${datePart}T00:00`);
                          } else {
                            const datePart =
                              current.length >= 16 ? current.slice(0, 10) : current.length === 10 ? current : '';
                            field.onChange(datePart);
                          }
                        }}
                        disabled={isSubmitting}
                      />
                    }
                    label="Set time"
                    sx={{
                      m: 0,
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                      alignSelf: 'center',
                      pr: 0.5,
                    }}
                  />
                </Box>
              );
            }}
          />
          <Stack spacing={1}>
            <Typography variant="subtitle2">Voucher User Expiry</Typography>
            {showAdvanceDescription
              ? 'These dates control what date is used when a voucher user for this voucher is created. Its is also used to determine the "liability" associated with a voucher.'
              : undefined}
            <RadioGroup
              row
              value={voucherUserExpiryMode}
              onChange={(e) => {
                const next = e.target.value as VoucherUserExpiryMode;
                voucherUserExpiryModeRef.current = next;
                setVoucherUserExpiryMode(next);
                if (next === 'days') {
                  setValue('voucherUserExpiresAt', '', { shouldDirty: true, shouldValidate: true });
                } else {
                  setValue('voucherUserExpiresInDays', '', { shouldDirty: true, shouldValidate: true });
                }
                void trigger(['voucherUserExpiresAt', 'voucherUserExpiresInDays']);
              }}
            >
              <FormControlLabel value="days" control={<Radio />} label="Expiry Days" disabled={isSubmitting} />
              <FormControlLabel value="date" control={<Radio />} label="Expiry Date" disabled={isSubmitting} />
            </RadioGroup>
            {voucherUserExpiryMode === 'date' && (
              <Controller
                name="voucherUserExpiresAt"
                control={control}
                rules={{
                  validate: (value) => {
                    if (voucherUserExpiryModeRef.current !== 'date') {
                      return true;
                    }
                    const at = (value || '').trim();
                    if (!at) {
                      return 'Enter a voucher user expiry date, or switch to expiry days from assignment.';
                    }
                    const showFromRaw = (getValues('showVoucherFromDate') || '').trim();
                    if (showFromRaw) {
                      const startDatePart = showFromRaw.length >= 10 ? showFromRaw.slice(0, 10) : showFromRaw;
                      const expiryDatePart = at.length >= 10 ? at.slice(0, 10) : at;
                      if (dayjs(startDatePart).isAfter(dayjs(expiryDatePart), 'day')) {
                        return 'Expiry date must be on or after the voucher start date.';
                      }
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Voucher user expires at"
                    required
                    type="date"
                    value={field.value ? dayjs(field.value).format('YYYY-MM-DD') : ''}
                    onChange={(event) => {
                      const localValue = event.target.value;
                      if (!localValue) {
                        field.onChange('');
                        return;
                      }
                      field.onChange(dayjs(localValue).format('YYYY-MM-DD'));
                    }}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ||
                      (showAdvanceDescription
                        ? 'Fixed expiry for issued voucher users (relative expiry uses Expiry Days).'
                        : undefined)
                    }
                    fullWidth
                    disabled={isSubmitting}
                    sx={highlightVoucherUserExpiryFields ? voucherUserExpiryWarningFieldSx : undefined}
                  />
                )}
              />
            )}
            {voucherUserExpiryMode === 'days' && (
              <Controller
                name="voucherUserExpiresInDays"
                control={control}
                rules={{
                  validate: (value) => {
                    if (voucherUserExpiryModeRef.current !== 'days') {
                      return true;
                    }
                    const trimmed = (value || '').trim();
                    if (!trimmed) {
                      return 'Enter expiry days from assignment, or switch to a fixed expiry date.';
                    }
                    const parsed = Number.parseInt(trimmed, 10);
                    if (Number.isNaN(parsed)) {
                      return 'Enter a valid whole number';
                    }
                    if (parsed < 0) {
                      return 'Must be 0 or greater';
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Voucher user expires in (days)"
                    required
                    type="number"
                    error={Boolean(fieldState.error)}
                    inputProps={{ min: 0 }}
                    helperText={
                      fieldState.error?.message ||
                      (showAdvanceDescription
                        ? 'Days from issue the voucher remains usable (fixed calendar expiry uses Expiry Date).'
                        : undefined)
                    }
                    fullWidth
                    disabled={isSubmitting}
                    sx={highlightVoucherUserExpiryFields ? voucherUserExpiryWarningFieldSx : undefined}
                  />
                )}
              />
            )}
            <VoucherLongExpiryPromoAdminAlert
              mode={voucherUserExpiryMode}
              voucherUserExpiresAt={watchedVoucherUserExpiresAt ?? ''}
              voucherUserExpiresInDays={watchedVoucherUserExpiresInDays ?? ''}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="theme"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Theme"
                  helperText={showAdvanceDescription ? 'Optional theme for the voucher display styling.' : undefined}
                  fullWidth
                  disabled={isSubmitting}
                >
                  {VOUCHER_THEME_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="productType"
              control={control}
              render={({ field }) => {
                const row = voucherProductTypeMenuRows.find((r) => r.key === field.value);
                const descriptionHint =
                  showAdvanceDescription &&
                  (row?.description?.trim() || 'Which product type this voucher aligns with (for presentation).');
                return (
                  <TextField
                    {...field}
                    select
                    label="Product Type"
                    helperText={descriptionHint || undefined}
                    fullWidth
                    disabled={isSubmitting || (isPromoMetaLoading && voucherProductTypeMenuRows.length === 0)}
                  >
                    {voucherProductTypeMenuRows.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }}
            />
          </Stack>
        </Stack>
        <Box mt={3}>
          <PageSubheader title="Rewards">
            {showAdvanceDescription && (
              <Box component="span" color="text.secondary" alignItems="left">
                The amount the voucher is worth, and for which products. Only one reward is applied to an order.
              </Box>
            )}
          </PageSubheader>
          <Controller
            name="voucherRewards"
            control={control}
            render={({ field }) => (
              <VoucherRewards
                rewards={field.value}
                conditions={voucherConditions}
                onChange={field.onChange}
                currencyCode={currencyCode}
                disabled={isSubmitting}
              />
            )}
          />
        </Box>
        <Box mt={3}>
          <PageSubheader title="Conditions" />
          <Controller
            name="voucherConditions"
            control={control}
            render={({ field }) => (
              <VoucherConditions
                conditions={field.value}
                rewards={voucherRewards}
                onChange={field.onChange}
                showAdvanceDescription={showAdvanceDescription}
                disabled={isSubmitting}
              />
            )}
          />
        </Box>
        <VoucherUsers
          userIds={voucherUsers}
          onChange={setVoucherUsers}
          expiredAt={expiredAt}
          onExpiredAtChange={setExpiredAt}
          voucherUserExpiresAt={watchedVoucherUserExpiresAt ?? ''}
          voucherUserExpiresInDays={watchedVoucherUserExpiresInDays ?? ''}
          showAdvanceDescription={showAdvanceDescription}
          disabled={isSubmitting}
          voucherId={voucherId}
          voucherName={voucherNameValue ?? ''}
        />
        {isEdit && voucherNameValue && (
          <VoucherRecentRequests voucherName={voucherNameValue} userId="" brand={brand} promoMeta={promoMeta} />
        )}
        <Box mt={3}>
          <PageSubheader title="Debug" />
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {isEdit && <DebugModal type="generic" title="Voucher API Response" data={voucherResponse?.result} />}
            <DebugModal type="generic" title="Promo Meta" data={promoMeta} />
          </Stack>
        </Box>
        {isEdit && voucherNameValue?.trim() && (
          <Box mt={3}>
            <VoucherCheck voucherName={voucherNameValue.trim()} />
          </Box>
        )}
        {isEdit && voucherId && (
          <Box mt={2}>
            <Divider />
            <Box mt={2}>
              <Button
                variant="text"
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleteVoucherMutation.isPending || isSubmitting}
              >
                Delete voucher
              </Button>
            </Box>
          </Box>
        )}
      </Stack>
    </Container>
  );
}
